/**
 * UI Store - Manages UI state (sidebar, modals, settings)
 * Uses Zustand for lightweight state management
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { Scenario, SearchMode } from '@/config/constants'

interface UIStoreState {
  // Sidebar
  sidebarCollapsed: boolean
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void

  // Theme
  isDarkMode: boolean
  toggleTheme: () => void
  setDarkMode: (isDark: boolean) => void

  // Settings
  selectedProjectId: string
  selectedScenario: Scenario
  selectedSearchMode: SearchMode
  setSelectedProjectId: (projectId: string) => void
  setScenario: (scenario: Scenario) => void
  setSearchMode: (mode: SearchMode) => void
  setSelectedScenario: (scenario: Scenario) => void
  setSelectedSearchMode: (mode: SearchMode) => void

  // Source tags
  activeTags: string[]
  setActiveTags: (tags: string[]) => void
  resetActiveTagsToDefaults: (defaults: string[]) => void

  // Reindexing
  isReindexing: boolean
  reindexProjectId: string | null
  setReindexing: (isReindexing: boolean, projectId?: string) => void

  // Modals
  isSettingsModalOpen: boolean
  setSettingsModalOpen: (open: boolean) => void
}

export const useUIStore = create<UIStoreState>()(
  devtools(
    persist(
      (set) => ({
        // Sidebar
        sidebarCollapsed: false,
        toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
        setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

        // Theme
        isDarkMode: false,
        toggleTheme: () => set((state) => ({ isDarkMode: !state.isDarkMode })),
        setDarkMode: (isDark) => set({ isDarkMode: isDark }),

        // Settings
        selectedProjectId: 'default',
        selectedScenario: 'default',
        selectedSearchMode: 'smart',
        setSelectedProjectId: (projectId) => set({ selectedProjectId: projectId }),
        setScenario: (scenario) => set({ selectedScenario: scenario }),
        setSearchMode: (mode) => set({ selectedSearchMode: mode }),
        setSelectedScenario: (scenario) => set({ selectedScenario: scenario }),
        setSelectedSearchMode: (mode) => set({ selectedSearchMode: mode }),

        // Source tags (not persisted — reset on project change)
        activeTags: [],
        setActiveTags: (tags) => set({ activeTags: tags }),
        resetActiveTagsToDefaults: (defaults) => set({ activeTags: defaults }),

        // Reindexing
        isReindexing: false,
        reindexProjectId: null,
        setReindexing: (isReindexing, projectId) =>
          set({
            isReindexing,
            reindexProjectId: projectId ?? null,
          }),

        // Modals
        isSettingsModalOpen: false,
        setSettingsModalOpen: (open) => set({ isSettingsModalOpen: open }),
      }),
      {
        name: 'ui-store',
        partialize: (state) => ({
          // Persist UI preferences
          sidebarCollapsed: state.sidebarCollapsed,
          isDarkMode: state.isDarkMode,
          selectedProjectId: state.selectedProjectId,
          selectedScenario: state.selectedScenario,
          selectedSearchMode: state.selectedSearchMode,
        }),
      }
    ),
    {
      name: 'ui-store',
    }
  )
)
